import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import AntdCustomButton from '../../../../components/buttons/AntdCustomButton';
import StepperModel from '../../../../models/StepperModel';
import { H1, H2, BodyText } from '../../../../components/ui/Typography';
import BusinessModel from '../../models/BusinessModel';
import { STEP_CATEGORY_KEYWORDS } from '../../../steps-definitions';
import ScreenNavigationModel, {
  ScreenType
} from '../../models/ScreenNavigationModel';
import { WIZARD_STEPS } from '../../../wizard-steps-description';
import type Step from '../../Step';
import HelpBoxCompanionModel from '../../../../models/HelpBoxCompanionModel';
import { motion, AnimatePresence } from 'framer-motion';
import {
  fadeInUpVariants,
  staggerContainerVariants
} from '../../../../components/animations/StepAnimations';

const CorporateCategory: React.FC = observer(() => {
  const { t, i18n } = useTranslation();
  const [isContinueLoading, setIsContinueLoading] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(true);
  const [nextScreen, setNextScreen] = React.useState<ScreenType | null>(null);

  // Navigation handlers
  const handleScreenChange = () => {
    setIsVisible(false);
  };

  const handleChangeCategory = () => {
    setNextScreen(ScreenType.CHOOSE_CORPORATE_CATEGORY);
    handleScreenChange();
  };

  const handleContinue = React.useCallback(async () => {
    const selectedBusiness = BusinessModel.getSelectedBusiness();
    const hasWebsite = selectedBusiness?.website && selectedBusiness.website.length > 0;

    if (!hasWebsite) {
      setNextScreen(ScreenType.ENTER_COMPANY_DOMAIN);
      handleScreenChange();
      return;
    }

    setIsContinueLoading(true);
    try {
      await (
        WIZARD_STEPS[StepperModel.currentStep].step as Step
      ).moveToNextStep();
    } catch (e) {
      console.error(e);
    } finally {
      setIsContinueLoading(false);
    }
  }, []);

  const helpBoxTitleMessage = t('What is a corporate category?');
  const helpBoxParagraphMessage = t("It corresponds to your industry and is based on the categories that Google uses to classify companies.");

  React.useEffect(() => {
    HelpBoxCompanionModel.setDisplayHelpBox(true);
    HelpBoxCompanionModel.setTitleMessage(helpBoxTitleMessage);
    HelpBoxCompanionModel.setParagraphMessage(helpBoxParagraphMessage);
  }, [i18n.language]);

  return (
    <AnimatePresence
      onExitComplete={() => {
        if (nextScreen) {
          setTimeout(() => {
            ScreenNavigationModel.setCurrentScreen(nextScreen);
          }, 200);
        }
      }}>
      {isVisible && (
        <motion.div
          className="flex items-start sm:items-center sm:text-center justify-start sm:justify-center flex-1"
          initial="hidden"
          animate="visible"
          variants={staggerContainerVariants}
          exit="hidden">
          <div className="">
            <motion.div variants={fadeInUpVariants}>
              <H1>{t('Corporate category')}</H1>
            </motion.div>

            <motion.div variants={fadeInUpVariants}>
              <BodyText>
                {t(
                  'The specification of the right business category is crucial for the success of your website.'
                )}
                <br />
                {t('Please check the following information:')}
              </BodyText>
            </motion.div>

            <motion.div variants={fadeInUpVariants}>
              <H2 style={{ fontWeight: 400 }}>
                {t('Your corporate category is:')}{' '}
                <span style={{ fontWeight: 700 }}>
                  "{BusinessModel.selectedCategoryName}"
                </span>
              </H2>
            </motion.div>

            <motion.div
              variants={fadeInUpVariants}
              className="flex sm:justify-center gap-[8px] mt-8"
            >
              <AntdCustomButton
                id="category-confirm-change"
                data-ga-event="category-confirm-change"
                appearance="default"
                disabled={isContinueLoading}
                onClick={handleChangeCategory}>
                {t('No, please change')}
              </AntdCustomButton>
              <AntdCustomButton
                id="category-confirm-next"
                data-ga-event="category-confirm-next"
                appearance="important"
                style={{ backgroundColor: '#52C41A' }}
                onClick={handleContinue}
                loading={isContinueLoading}>
                {t('Yes, continue')}
              </AntdCustomButton>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default CorporateCategory;
